import { useEffect, useState } from 'react'
import { listEvents, listSpeakers, listTags } from '../lib/api.js'
import ErrorAlert from './ErrorAlert.jsx'
import ConferenceCard from './ConferenceCard.jsx'
import EventCard from './EventCard.jsx'
import Filters from './Filters.jsx'
import { groupByConference, toConferenceWithTalks } from '../lib/grouping.js'
import { includesPast } from '../lib/dateRange.js'

const PAGE_SIZE = 10

function paramsForApi(filters, page) {
  return {
    speaker_id: filters.speakerId || undefined,
    tag: filters.tag || undefined,
    country: filters.country || undefined,
    region: filters.region || undefined,
    city: filters.city || undefined,
    from: filters.from ? new Date(`${filters.from}T00:00:00Z`).toISOString() : undefined,
    to: filters.to ? new Date(`${filters.to}T23:59:59Z`).toISOString() : undefined,
    page,
    size: PAGE_SIZE,
  }
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/60 sm:p-6">
      <div className="flex flex-col gap-5 sm:flex-row">
        <div className="h-16 w-20 rounded-xl shimmer sm:h-20 sm:w-[88px]" />
        <div className="flex-1 space-y-3">
          <div className="flex gap-2">
            <div className="h-5 w-16 rounded-full shimmer" />
            <div className="h-5 w-12 rounded-full shimmer" />
          </div>
          <div className="h-5 w-3/4 rounded shimmer" />
          <div className="h-4 w-1/2 rounded shimmer" />
          <div className="h-4 w-2/3 rounded shimmer" />
        </div>
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200/60">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-600">
        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path strokeLinecap="round" d="M3 9h18M8 3v4M16 3v4" />
        </svg>
      </div>
      <h3 className="mt-3 text-base font-semibold text-slate-900">No events match these filters</h3>
      <p className="mt-1 text-sm text-slate-500">
        Try clearing a filter or widening the date range.
      </p>
    </div>
  )
}

// #338: filters come from the URL now (see useFilterRoute), so the list is a
// pure function of the address bar. `filters` and `onFiltersChange` are props
// rather than state — the page that owns the route owns them.
export default function EventList({ onCountChange, filters = {}, onFiltersChange }) {
  const [page, setPage] = useState(0)
  const [items, setItems] = useState([])
  const [totalElements, setTotalElements] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [speakers, setSpeakers] = useState([])
  const [tags, setTags] = useState([])

  useEffect(() => {
    listSpeakers().then(setSpeakers).catch(() => {})
    listTags().then(setTags).catch(() => {})
  }, [])

  // Reset to the first page whenever the query changes. Keyed on the
  // serialised filters, not the object: it is rebuilt on every render now that
  // it is derived from the URL, and object identity would reset the page (and
  // refetch) on every render.
  const filterKey = JSON.stringify(filters)
  useEffect(() => {
    setPage(0)
  }, [filterKey])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listEvents(paramsForApi(filters, page))
      .then((data) => {
        if (cancelled) return
        const content = data.content || []
        setItems((prev) => (page === 0 ? content : [...prev, ...content]))
        setTotalElements(data.totalElements || 0)
      })
      .catch((err) => {
        if (!cancelled) setError(err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, page])

  // #334: the count alone is no longer enough for the strapline — once the
  // selected range reaches into the past, "N upcoming talks" is simply false.
  const pastIncluded = includesPast(filters)

  useEffect(() => {
    if (onCountChange) onCountChange(totalElements, { pastIncluded })
  }, [totalElements, pastIncluded, onCountChange])

  const hasMore = items.length < totalElements
  const showSkeleton = loading && items.length === 0 && !error

  return (
    <div className="space-y-4">
      <Filters value={filters} onChange={onFiltersChange} speakers={speakers} tags={tags} />

      {error ? (
        <ErrorAlert
          title="Couldn't load events"
          message={error.message || String(error)}
          source="EventList"
        />
      ) : null}

      {showSkeleton ? (
        <ul className="space-y-3">
          <li><SkeletonCard /></li>
          <li><SkeletonCard /></li>
          <li><SkeletonCard /></li>
        </ul>
      ) : null}

      {!loading && !error && items.length === 0 ? <EmptyState /> : null}

      {items.length > 0 ? (
        <ul className="space-y-3">
          {/* #308: talks sharing a conference collapse into one container card
              carrying the conference's date range and its talk list. Standalone
              talks still render as an EventCard, unchanged. */}
          {groupByConference(items).map((row) => (
            <li key={row.id}>
              {row.type === 'conference' ? (
                <ConferenceCard conference={toConferenceWithTalks(row)} />
              ) : (
                <EventCard event={row.event} />
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {hasMore ? (
        <div className="text-center">
          <button
            type="button"
            disabled={loading}
            onClick={() => setPage((p) => p + 1)}
            className="inline-flex min-h-[44px] items-center rounded-full bg-white px-5 py-2 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Load more events'}
          </button>
        </div>
      ) : null}
    </div>
  )
}
